import { gql, useQuery } from '@apollo/client';
import {
  Alert, Icon, Hyperlink, Skeleton,
} from '@openedx/paragon';
import {
  RiBuildingLine,
  RiMailLine,
  RiPhoneLine,
  RiGlobalLine,
  RiMapPinLine,
} from '@remixicon/react';
import { useIntl } from '@edx/frontend-platform/i18n';

import messages from './messages';
import './About.scss';

const GET_ORG_DETAILS = gql`
  query GetOrgDetailsAbout {
    organisationDetails {
      id
      email
      country
      address
      adminName
      contactNo
      metadata
    }
  }
`;

const About = () => {
  const intl = useIntl();
  const { data, loading, error } = useQuery(GET_ORG_DETAILS);

  if (loading) {
    return (
      <div className="learner-about">
        <h1 className="learner-about__title">
          {intl.formatMessage(messages.pageTitle)}
        </h1>
        <Skeleton count={4} height={40} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="learner-about">
        <Alert variant="danger">
          {intl.formatMessage(messages.loadError)}
        </Alert>
      </div>
    );
  }

  const org = data?.organisationDetails?.[0] || {};
  const links = org?.metadata?.links || {};

  const infoItems = [
    { key: 'organizationName', icon: RiBuildingLine, value: org.adminName },
    { key: 'contactEmail', icon: RiMailLine, value: org.email },
    { key: 'phoneNumber', icon: RiPhoneLine, value: org.contactNo },
    { key: 'country', icon: RiGlobalLine, value: org.country },
  ];

  return (
    <div className="learner-about">
      <h1 className="learner-about__title">
        {intl.formatMessage(messages.pageTitle)}
      </h1>

      <section className="learner-about__section">
        <h2 className="learner-about__section-title">
          {intl.formatMessage(messages.contactInformation)}
        </h2>
        <div className="learner-about__info-grid">
          {infoItems.map(({ key, icon, value }) => (
            <div key={key} className="learner-about__info-item">
              <Icon className="learner-about__info-icon" src={icon} size="sm" />
              <div>
                <div className="learner-about__info-label">
                  {intl.formatMessage(messages[key])}
                </div>
                <div className="learner-about__info-value">{value || '—'}</div>
              </div>
            </div>
          ))}
          {org.address && (
            <div className="learner-about__info-item learner-about__info-item--full">
              <Icon className="learner-about__info-icon" src={RiMapPinLine} size="sm" />
              <div>
                <div className="learner-about__info-label">
                  {intl.formatMessage(messages.address)}
                </div>
                <div className="learner-about__info-value">{org.address}</div>
              </div>
            </div>
          )}
        </div>
      </section>

      {Object.keys(links).length > 0 && (
        <section className="learner-about__section">
          <h2 className="learner-about__section-title">
            {intl.formatMessage(messages.linksTitle)}
          </h2>
          <p className="learner-about__section-description">
            {intl.formatMessage(messages.linksDescription)}
          </p>
          <div className="learner-about__links-grid">
            {Object.entries(links).map(([name, url]) => (
              <div key={name} className="learner-about__link-item">
                <span className="learner-about__link-name">{name}</span>
                <Hyperlink destination={url} target="_blank">
                  {url}
                </Hyperlink>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default About;
